import { useEffect, useState } from 'react';
import { FaClock, FaEnvelope, FaPhone, FaSearch, FaSignOutAlt, FaTrash, FaUser } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchContacts();
  }, [navigate]);

  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/contact`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      } else if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    navigate('/admin/login');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/contact/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        setContacts(contacts.filter(c => c._id !== id));
        if (selectedContact?._id === id) {
          setSelectedContact(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete contact:', error);
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Loading contacts...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1>Admin Dashboard</h1>
          <div className="admin-actions">
            <span className="admin-email">
              {localStorage.getItem('adminEmail')}
            </span>
            <button onClick={handleLogout} className="btn btn-logout">
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="admin-content">
        <div className="admin-sidebar">
          <div className="search-box">
            <FaSearch />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="contacts-summary">
            <h3>Contact Submissions ({filteredContacts.length})</h3>
          </div>

          <div className="contacts-list">
            {filteredContacts.length === 0 ? (
              <p className="no-contacts">No contacts found</p>
            ) : (
              filteredContacts.map((contact) => (
                <div
                  key={contact._id}
                  className={`contact-item ${selectedContact?._id === contact._id ? 'active' : ''}`}
                  onClick={() => setSelectedContact(contact)}
                >
                  <div className="contact-item-header">
                    <h4>{contact.name}</h4>
                    <span className="contact-date">{formatDate(contact.createdAt)}</span>
                  </div>
                  <p className="contact-subject">{contact.subject}</p>
                  <p className="contact-email">{contact.email}</p>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="admin-main">
          {selectedContact ? (
            <div className="contact-detail">
              <div className="contact-detail-header">
                <h2>{selectedContact.subject}</h2>
                <button
                  onClick={() => handleDelete(selectedContact._id)}
                  className="btn btn-danger"
                >
                  <FaTrash /> Delete
                </button>
              </div>

              <div className="contact-detail-meta">
                <div className="meta-item">
                  <FaUser />
                  <div>
                    <label>Name</label>
                    <span>{selectedContact.name}</span>
                  </div>
                </div>

                <div className="meta-item">
                  <FaEnvelope />
                  <div>
                    <label>Email</label>
                    <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a>
                  </div>
                </div>

                {selectedContact.phone && (
                  <div className="meta-item">
                    <FaPhone />
                    <div>
                      <label>Phone</label>
                      <a href={`tel:${selectedContact.phone}`}>{selectedContact.phone}</a>
                    </div>
                  </div>
                )}

                <div className="meta-item">
                  <FaClock />
                  <div>
                    <label>Received</label>
                    <span>{formatDate(selectedContact.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className="contact-detail-message">
                <h3>Message</h3>
                <p>{selectedContact.message}</p>
              </div>

              <div className="contact-detail-actions">
                <a
                  href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                  className="btn btn-primary"
                >
                  <FaEnvelope /> Reply via Email
                </a>
                {selectedContact.phone && (
                  <a
                    href={`tel:${selectedContact.phone}`}
                    className="btn btn-secondary"
                  >
                    <FaPhone /> Call
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="no-selection">
              <FaEnvelope size={64} />
              <h3>Select a contact to view details</h3>
              <p>Choose a contact from the list to see full information</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
